<script setup>
import { computed } from 'vue';

import MessageMeta from '../MessageMeta.vue';
import Icon from 'next/icon/Icon.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
});

const { variant, orientation, inReplyTo, shouldGroupWithNext, contentAttributes } =
  useMessageContext();
const { t } = useI18n();

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-n-solid-blue text-n-slate-12',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-solid-amber text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-n-slate-4 text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const isDirectSend = computed(() => {
  const attrs = contentAttributes?.value || {};
  return attrs.direct_send === true || attrs.directSend === true;
});

const messageClass = computed(() => {
  if (isDirectSend.value) {
    return [
      'rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm right-bubble',
      'direct-send-bubble',
    ];
  }

  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

const shouldShowMeta = computed(
  () =>
    !props.hideMeta &&
    !shouldGroupWithNext.value &&
    variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

const metaReferral = computed(() => {
  const attrs = contentAttributes?.value || {};
  return attrs.meta_referral || attrs.metaReferral || null;
});

const referralSourceLabel = computed(() => {
  if (!metaReferral.value) return '';
  const type = metaReferral.value.source_type || metaReferral.value.sourceType;
  if (type === 'ad') return t('CONVERSATION.META_REFERRAL.AD');
  if (type === 'post') return t('CONVERSATION.META_REFERRAL.POST');
  return t('CONVERSATION.META_REFERRAL.INSTAGRAM');
});

const referralSourceUrl = computed(() => {
  if (!metaReferral.value) return null;
  return metaReferral.value.source_url || metaReferral.value.sourceUrl;
});

const replyToPreview = computed(() => {
  if (!inReplyTo) return '';

  const { content, attachments } = inReplyTo.value;

  if (content) return new MessageFormatter(content).formattedMessage;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});
</script>

<template>
  <div
    class="text-sm"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
        'direct-send-colors': isDirectSend,
      },
    ]"
    :style="isDirectSend ? { backgroundColor: '#bae6fd' } : {}"
  >
    <a
      v-if="metaReferral"
      :href="referralSourceUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="flex items-center gap-2 p-2 -mx-1 mb-2 rounded-lg bg-n-alpha-black1 no-underline text-current"
    >
      <Icon icon="i-lucide-instagram" class="size-4 shrink-0 text-n-slate-11" />
      <span class="text-xs font-medium truncate">
        {{ referralSourceLabel }}
      </span>
      <span
        v-if="metaReferral.headline || metaReferral.body"
        class="text-xs text-n-slate-11 truncate"
      >
        {{ metaReferral.headline || metaReferral.body }}
      </span>
    </a>
    <div
      v-if="inReplyTo"
      class="p-2 -mx-1 mb-2 rounded-lg cursor-pointer bg-n-alpha-black1"
      @click="scrollToMessage"
    >
      <div
        v-dompurify-html="replyToPreview"
        class="prose prose-bubble line-clamp-2"
      />
    </div>
    <div
      v-if="isDirectSend"
      class="flex items-center gap-1 mb-1 text-xs font-medium opacity-70"
      style="color: #0369a1"
    >
      <Icon icon="i-lucide-send" class="size-3" />
      Envio direto não oficial
    </div>
    <slot />
    <MessageMeta
      v-if="shouldShowMeta"
      :class="[
        flexOrientationClass,
        variant === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : '',
        variant === MESSAGE_VARIANTS.PRIVATE
          ? 'text-n-amber-12/50'
          : 'text-n-slate-11',
      ]"
      class="mt-2"
    />
  </div>
</template>

<style>
.direct-send-colors,
.direct-send-colors * {
  color: #0c4a6e !important;
}
</style>
